import { LoginCredentials, AuthResponse } from "../types/admin";
import { Hotel, HotelFilter } from "../types/hotel";
import { Guest, GuestFilter, Booking } from "../types/guest";
import { mockGuests, mockBookings } from "../utils/mockData";
import { BASE_URL } from "../config";

const USE_MOCK_DATA = false;

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

class ApiService {

  /* -----------------------------------------
     CORE REQUEST HANDLER
  ----------------------------------------- */

  private async request<T>(
    endpoint: string,
    method: HttpMethod = "GET",
    body?: unknown
  ): Promise<T> {

    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || "API request failed");
    }

    return data;
  }

  private buildQuery(params?: Record<string, any>): string {

    if (!params) return "";

    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        query.append(key, String(value));
      }
    });

    return query.toString();
  }

  /* -----------------------------------------
     AUTH
  ----------------------------------------- */

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return this.request<AuthResponse>("/admin/login", "POST", credentials);
  }

  logout(): void {
    localStorage.removeItem("admin");
  }

  /* -----------------------------------------
     HOTELS / PROPERTIES
  ----------------------------------------- */

  async getHotels(filters?: HotelFilter): Promise<{ data: Hotel[]; total: number }> {
    const query = this.buildQuery(filters);
    return this.request(`/properties/list?${query}`);
  }


  async getHotelById(id: string): Promise<any> {
    return this.request(`/properties/${id}`);
  }

  async getDashboardData(): Promise<any> {
    return this.request(`/admin/dashboard`);
  }

  async createHotel(data: Omit<Hotel, "id" | "createdAt" | "updatedAt">): Promise<Hotel> {
    return this.request("/hotels", "POST", data);
  }

  async updateHotel(id: string, data: Partial<Hotel>): Promise<Hotel> {
    return this.request(`/hotels/${id}`, "PUT", data);
  }

  async deleteHotel(id: string): Promise<void> {

    if (USE_MOCK_DATA) return;

    await this.request(`/hotels/${id}`, "DELETE");
  }

  /* -----------------------------------------
     PROPERTY VERIFICATION
  ----------------------------------------- */

  approveHotel(id: string) {
    return this.request(`/properties/approve/${id}`, "POST");
  }

  rejectHotel(id: string, reason: string) {
    return this.request(`/properties/reject/${id}`, "POST", { reason });
  }

  /* -----------------------------------------
     GUESTS
  ----------------------------------------- */

  // async getCustomers(filters?: GuestFilter): Promise<{ data: Guest[]; total: number }> {

  //   if (USE_MOCK_DATA) {

  //     let filtered = [...mockGuests];

  //     if (filters?.search) {

  //       const search = filters.search.toLowerCase();

  //       filtered = filtered.filter(
  //         (g) =>
  //           `${g.firstName} ${g.lastName}`.toLowerCase().includes(search) ||
  //           g.email.toLowerCase().includes(search)
  //       );
  //     }

  //     if (filters?.country) {
  //       filtered = filtered.filter((g) => g.country === filters.country);
  //     }

  //     return {
  //       data: filtered.slice(0, filters?.limit || 10),
  //       total: filtered.length,
  //     };
  //   }

  //   const query = this.buildQuery(filters);

  //   return this.request(`/guests?${query}`);
  // }

  // getGuestById(id: string) {
  //   return this.request<Guest>(`/guests/${id}`);
  // }

  // updateGuest(id: string, data: Partial<Guest>) {
  //   return this.request<Guest>(`/guests/${id}`, "PUT", data);
  // }

  /* -----------------------------------------
     LOCATIONS
  ----------------------------------------- */

  async getRegions(): Promise<{ label: string; value: string }[]> {

    const response = await this.request<any>("/locations/regions");

    return (response?.data || []).map((r: any) => ({
      label: r.name,
      value: r.id.toString(),
    }));
  }

  async getDistricts(regionId: string): Promise<{ label: string; value: string }[]> {

    const response = await this.request<any>(
      `/locations/districts?region_id=${regionId}`
    );

    return (response?.data || []).map((d: any) => ({
      label: d.name,
      value: d.id.toString(),
    }));
  }

  /* -----------------------------------------
     AMENITIES
  ----------------------------------------- */

  async getAmenities(): Promise<{ label: string; value: string; category?: string }[]> {

    try {

      const response = await this.request<any>("/amenities");

      return (response?.data || []).map((a: any) => ({
        label: a.name,
        value: a.id.toString(),
        category: a.category,
      }));

    } catch {
      return [];
    }
  }

  /* -----------------------------------------
     ACCOMMODATION TYPES
  ----------------------------------------- */

  async getAccommodationTypes(): Promise<{ label: string; value: string }[]> {

    const response = await this.request<any>(
      "/locations/accommodation-types"
    );

    return (response?.data || []).map((t: any) => ({
      label: t.name,
      value: t.id.toString(),
    }));
  }

  /* -----------------------------------------
     PROPERTY CREATE / UPDATE
  ----------------------------------------- */

  async createProperty(formData: FormData) {

    const response = await fetch(`${BASE_URL}/properties`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to create property");
    }

    return response.json();
  }

  async updateProperty(id: string, formData: FormData) {

    const response = await fetch(`${BASE_URL}/properties/update/${id}`, {
      method: "POST",
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result?.message || "Failed to update property");
    }

    return result;
  }


  searchProperties(params: Record<string, any>) {

    const query = this.buildQuery(params);

    return this.request(`/properties/search?${query}`);
  }
  /* -----------------------------------------
     BOOKINGS
  ----------------------------------------- */

  async getBookings(page = 1, limit = 10): Promise<{ data: Booking[]; total: number }> {

    if (USE_MOCK_DATA) {

      const start = (page - 1) * limit;

      return {
        data: mockBookings.slice(start, start + limit),
        total: mockBookings.length,
      };
    }

    return this.request(`/bookings?page=${page}&limit=${limit}`);
  }

  getBookingById(id: string) {
    return this.request<Booking>(`/bookings/${id}`);
  }

  updateBookingStatus(id: string, status: string) {
    return this.request<Booking>(`/bookings/${id}`, "PUT", { status });
  }

  async getCustomers(params?: { startDate?: string; endDate?: string, search?: string }):
    Promise<{ data: Guest[]; total: number }> {

    const query = new URLSearchParams();

    if (params?.startDate) query.append("startDate", params.startDate);
    if (params?.endDate) query.append("endDate", params.endDate);
    if (params?.search) query.append("searchParam", params.search);

    const url = `${BASE_URL}/customers/list${query.toString() ? `?${query.toString()}` : ""}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error("Failed to fetch guests");
    }

    const json = await response.json();

    return {
      data: json.data || [],
      total: (json.data || []).length
    };
  }

  async getCustomerById(id: string): Promise<Guest> {
    const response = await this.request<{ data: Guest }>(
      `/customers/${id}`
    );

    return response.data;
  }



  async updateCustomers(
    id: string,
    data: {
      name: string;
      phone: string;
      email: string;
    }
  ): Promise<Guest> {
    const response = await this.request<{ data: Guest }>(
      `/customers/${id}`,
      "PUT",
      data
    );
    return response.data;
  }

  // async updateCustomers(
  //   id: string,
  //   data: {
  //     name: string;
  //     phone: string;
  //     email: string;
  //   }
  // ): Promise<Guest> {

  //   const response = await this.request<{ data: Guest }>(
  //     `/customers/${id}`,
  //     "PUT",
  //     data
  //   );

  //   return response.data;
  // }

  // Fetch guest by ID
  async getGuestById(id: string): Promise<Guest> {
    const response = await fetch(`${BASE_URL}/profile/${id}`);
    if (!response.ok) throw new Error("Failed to fetch guest");
    const json = await response.json();
    return json.data;
  }



  // Update guest
  // async updateGuest(id: string, guestData: Partial<Guest>): Promise<Guest> {
  //   const response = await fetch(`${BASE_URL}/profile/${id}`, {
  //     method: "PUT",
  //     headers: { "Content-Type": "application/json" },
  //     body: JSON.stringify(guestData),
  //   });
  //   if (!response.ok) throw new Error("Failed to update guest");
  //   const json = await response.json();
  //   return json.data;
  // }
  // Update guest


  /* -----------------------------------------
    OWNERS Update
 ----------------------------------------- */

  async updateOwner(data: {
    id: number;
    name?: string;
    email?: string;
    phone?: string;
  }): Promise<any> {

    const payload = {
      name: data.name,
      email: data.email,
      phone: data.phone,
    };

    const response = await fetch(`${BASE_URL}/owners/${data.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const json = await response.json().catch(() => ({}));

    if (!response.ok) {
      console.error("Update Owner API Error:", json);
      throw new Error(json?.message || "Failed to update owner");
    }

    return json;
  }

  /* -----------------------------------------
     OWNERS
  ----------------------------------------- */

  async getOwnersOld(): Promise<any[]> {

    const response = await this.request<{ data: any[] }>("/owners");

    return response.data;
  }


/* -----------------------------------------
     PROPERTY PHOTOS
  ----------------------------------------- */

  async addPropertyPhoto(
    propertyId: string,
    file: File,
    caption?: string,
    displayOrder?: number
  ): Promise<any> {

    const formData = new FormData();
    formData.append("propertyPhoto", file);
    if (caption) formData.append("caption", caption);
    if (displayOrder !== undefined) formData.append("displayOrder", String(displayOrder));

    const response = await fetch(`${BASE_URL}/properties/${propertyId}/photos`, {
      method: "POST",
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result?.message || "Failed to add photo");
    }

    return result;
  }

  async deletePropertyPhoto(photoId: number | string): Promise<any> {

    const response = await fetch(`${BASE_URL}/properties/photos/${photoId}`, {
      method: "DELETE",
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result?.message || "Failed to delete photo");
    }

    return result;
  }
  async getOwners(params?: { startDate?: string; endDate?: string, search?: string }):
    Promise<any[]> {

    const query = new URLSearchParams();

    if (params?.startDate) query.append("startDate", params.startDate);
    if (params?.endDate) query.append("endDate", params.endDate);
    if (params?.search) query.append("searchParam", params.search);

    const url = `${BASE_URL}/owners/list${query.toString() ? `?${query.toString()}` : ""}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error("Failed to fetch guests");
    }

    const json = await response.json();

    return json.data || [];
  }


  getOwnerById(id: string) {
    return this.request(`/owners/${id}`);
  }

  createOwner(data: {
    owner_name: string;
    hotel_name: string;
    email?: string;
    phone?: string;
  }) {
    return this.request("/owners", "POST", data);
  }

  // updateOwner(
  //   id: number,
  //   data: {
  //     owner_name: string;
  //     hotel_name: string;
  //     email?: string;
  //     phone?: string;
  //   }
  // ) {
  //   return this.request(`/owners/update/${id}`, "POST", data);
  // }
}
export const apiService = new ApiService();
